import { useState } from "react";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/shared/hooks/use-auth";
import { LoginPage } from "@/shared/ui/LoginPage";
import { ControlPanel } from "@/shared/ui/ControlPanel";
import { useControlPanelStore } from "@/shared/ui/control-panel-store";
import { Sidebar } from "@/modules/chat/components/Sidebar";
import { useLayoutStore } from "@/modules/chat/layout/LayoutProvider";
import { useMessages, useStreaming } from "@/modules/chat/stores";
import { useSessions } from "@/modules/chat/hooks/use-sessions";
import { useSessionEvents } from "@/modules/chat/hooks/use-session-events";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";
import {
  invalidateSpaceScopedQueries,
  personalSpaceQueryOptions,
} from "@/modules/spaces/spaces-queries";
import { useSpaceStore } from "@/modules/spaces/space-store";

type Auth = ReturnType<typeof useAuth>;

/**
 * Pathless layout that owns the app shell — auth gate, the chat-layout
 * frame, the sidebar, and the optional ControlPanel takeover. Every
 * authenticated route (`/`, `/history`, `/agents`, …) is a child of this
 * layout, so navigating between them only swaps `<Outlet />` content.
 * Sidebar stays mounted, no flicker.
 *
 * Children attach via flat naming: `_app.index.tsx`, `_app.history.tsx`, …
 */
export const Route = createFileRoute("/_app")({
  component: AppLayout,
});

function AppLayout() {
  const auth = useAuth();

  if (auth.loading) return <div className="auth-loading">Загрузка...</div>;
  if (!auth.isAuthenticated) {
    return <LoginPage onLogin={auth.login} onRegister={auth.register} />;
  }
  // Authenticated hooks (queries, stores) live in a child component so
  // they only mount when there's a token. Without this split, hook order
  // forces `useQuery(personalSpaceQueryOptions())` and `useSessions()` to
  // run even on the logout transition — they fire 401s, the old reload
  // handler restarted the cycle, and the result was a tight loop.
  return <AuthenticatedShell auth={auth} />;
}

function AuthenticatedShell({ auth }: { auth: Auth }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const showControl = useControlPanelStore((s) => s.open);
  const closeControl = useControlPanelStore((s) => s.close);
  const openControl = useControlPanelStore((s) => s.openControl);
  const { mainView } = useLayoutStore();
  const messages = useMessages();
  const isStreaming = useStreaming();
  const setCurrentSpaceId = useSpaceStore((s) => s.setCurrentSpaceId);
  const setPendingAgentRef = useAgentDraftStore((s) => s.setPendingRef);
  const { data: personalSpace } = useQuery(personalSpaceQueryOptions());
  const { startNew } = useSessions();
  const [collapsed, setCollapsed] = useState(false);
  // Long-lived SSE that invalidates the sessions cache when any run halts
  // or finishes — keeps «История» counter and «Требуют действия» fresh on
  // tabs that aren't currently streaming the chat themselves.
  useSessionEvents();

  // ControlPanel is a full-screen takeover; it replaces the whole shell
  // (no sidebar, no chat-layout). Keeping it here means any child route
  // can open it via `useControlPanelStore` and look identical.
  if (showControl) {
    return (
      <ControlPanel
        isAdmin={auth.user?.role === "admin"}
        currentUserId={auth.user?.id ?? ""}
        onBack={closeControl}
      />
    );
  }

  // Layout modifiers reflect chat-only state (canvas / docx editor open).
  // On section routes mainView is always null, so no modifier kicks in.
  const mainActive = mainView !== null;
  const heroMode = !isStreaming && messages.length === 0;
  const splitMode = mainActive && !mainView.expanded && !heroMode;
  const expandedMode = mainActive && !splitMode;

  return (
    <div
      className={`chat-layout${splitMode ? " chat-layout--split" : ""}${expandedMode ? " chat-layout--main" : ""}`}
    >
      <Sidebar
        onStartNew={() => {
          setPendingAgentRef(null);
          // «Новая задача» всегда personal — независимо от текущего
          // активного пространства.
          const cur = useSpaceStore.getState().currentSpaceId;
          if (personalSpace && personalSpace.id !== cur) {
            setCurrentSpaceId(personalSpace.id);
            invalidateSpaceScopedQueries(qc);
          }
          // startNew navigates to `/new` (the hero route) — no separate
          // navigate needed.
          startNew();
        }}
        userName={auth.user?.name || auth.user?.email || ""}
        isAdmin={auth.user?.role === "admin"}
        onLogout={() => {
          auth.logout();
          navigate({ to: "/" });
        }}
        onOpenControl={() => {
          // ControlPanel — admin-only. Не-админам прячем некуда не
          // ведущую кнопку, отправляя их на каталог пространств.
          if (auth.user?.role === "admin") openControl();
          else navigate({ to: "/spaces" });
        }}
        collapsed={collapsed}
        onToggleSidebar={() => setCollapsed((c) => !c)}
      />
      <Outlet />
    </div>
  );
}
